import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  isDefaultActiveCriterion,
  PLATFORM_PLACEMENTS,
  PREFLIGHT_CRITERIA,
  type PreflightCriterionDefinition,
  type PreflightPlatformCode,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  cn,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@nexus/ui";
import { PlatformLabel } from "../PlatformLabel/PlatformLabel";

const PLATFORM_CODES = Object.keys(
  PLATFORM_PLACEMENTS,
) as PreflightPlatformCode[];

function formatDefaultWeightPercent(weight: number): string {
  return `${weight.toFixed(2)}%`;
}

function criteriaForPlatform(platform: PreflightPlatformCode) {
  return PREFLIGHT_CRITERIA.filter((c) => c.platform === platform).sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );
}

function CriterionDefinitions({ criterion }: { criterion: PreflightCriterionDefinition }) {
  const { t } = useTranslation();
  const catalogueKey = `preflight.catalogue.${criterion.platform}.${criterion.code}`;
  const byAssetType = criterion.definitionByAssetType;

  if (byAssetType?.IMAGE && byAssetType?.VIDEO) {
    return (
      <div className="text-muted-foreground mt-1 flex flex-col gap-1 text-xs leading-snug">
        <p>
          <span className="text-foreground font-medium">
            {t("preflight.scoringDrawer.assetTypeImage")}
            {" "}
          </span>
          {t(`${catalogueKey}.definitionImage`, byAssetType.IMAGE)}
        </p>
        <p>
          <span className="text-foreground font-medium">
            {t("preflight.scoringDrawer.assetTypeVideo")}
            {" "}
          </span>
          {t(`${catalogueKey}.definitionVideo`, byAssetType.VIDEO)}
        </p>
      </div>
    );
  }

  return (
    <p className="text-muted-foreground mt-1 text-xs leading-snug">
      {t(`${catalogueKey}.definition`, criterion.definition)}
    </p>
  );
}

function CriterionCard({
  criterion,
  activeLabel,
  inactiveLabel,
  dash,
}: {
  criterion: PreflightCriterionDefinition;
  activeLabel: string;
  inactiveLabel: string;
  dash: string;
}) {
  const { t } = useTranslation();
  const isActive = isDefaultActiveCriterion(criterion);
  const name = t(`preflight.catalogue.${criterion.platform}.${criterion.code}.name`, criterion.name);

  return (
    <li
      className={cn(
        "border-border flex flex-col gap-2 rounded-lg border bg-card p-3",
        !isActive && "opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-foreground text-sm font-medium">
              {name}
            </p>
            <Badge variant={isActive ? "status-connected" : "outline"}>
              {isActive ? activeLabel : inactiveLabel}
            </Badge>
          </div>
          <CriterionDefinitions criterion={criterion} />
        </div>
        <span className="text-foreground shrink-0 text-xs tabular-nums">
          {isActive ? formatDefaultWeightPercent(criterion.defaultWeight) : dash}
        </span>
      </div>
    </li>
  );
}

export interface ScoringLogicDrawerProps {
  /** Platform the drawer opens on; users can switch tabs once inside (KTD8). */
  platform: PreflightPlatformCode;
  /** Custom trigger; defaults to a "How scoring works" outline button. Pass `null` for controlled-only (no trigger). */
  trigger?: ReactNode | null;
  /** Controlled open state (optional). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * View-only drawer listing the static criterion catalogue (`PREFLIGHT_CRITERIA`)
 * per platform with v1 default weights and active/inactive status. Per-report
 * weights in the breakdown may differ; these are the seeded defaults (KTD8).
 */
export function ScoringLogicDrawer({
  platform,
  trigger,
  open,
  onOpenChange,
}: ScoringLogicDrawerProps) {
  const { t } = useTranslation();
  const [activePlatform, setActivePlatform] =
    useState<PreflightPlatformCode>(platform);

  useEffect(() => setActivePlatform(platform), [platform]);

  const criteria = useMemo(
    () => criteriaForPlatform(activePlatform),
    [activePlatform],
  );

  const cardLabels = {
    activeLabel: t("preflight.scoringDrawer.active"),
    inactiveLabel: t("preflight.scoringDrawer.inactive"),
    dash: t("preflight.dash"),
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger !== null ? (
        <SheetTrigger asChild>
          {trigger ?? (
            <Button type="button" variant="outline" size="sm">
              {t("preflight.scoringDrawer.trigger")}
            </Button>
          )}
        </SheetTrigger>
      ) : null}
      <SheetContent className="w-full sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>{t("preflight.scoringDrawer.title")}</SheetTitle>
          <SheetDescription>
            {t("preflight.scoringDrawer.description")}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-0 px-0 py-0">
          <div
            className="border-border flex shrink-0 gap-1 overflow-x-auto border-b px-6 pt-1 [scrollbar-width:none]! [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label={t("preflight.scoringDrawer.platformAriaLabel")}
          >
            {PLATFORM_CODES.map((code) => {
              const isSelected = activePlatform === code;
              return (
                <button
                  key={code}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => setActivePlatform(code)}
                  className={cn(
                    "-mb-px shrink-0 border-b-2 px-3 py-2 text-[13px] transition-colors",
                    isSelected
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <PlatformLabel platform={code} />
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            className="flex flex-col gap-3 px-6 py-4"
          >
            <ol className="flex flex-col gap-3">
              {criteria.map((criterion) => (
                <CriterionCard
                  key={criterion.code}
                  criterion={criterion}
                  {...cardLabels}
                />
              ))}
            </ol>
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
